import React from "react";
import ReactMarkdown from "react-markdown";

const MOBILE_QUERY = "(max-width: 767px)";

export default function MediaMarkdownListSection({ section, renderRight }) {
  if (!section || typeof section !== "object") throw new Error("MediaMarkdownListSection requires section object");
  const items = section.items;
  if (!Array.isArray(items) || items.length === 0) throw new Error("MediaMarkdownListSection requires section.items array");

  const [activeIndex, setActiveIndex] = React.useState(null);
  const [isMobile, setIsMobile] = React.useState(false);

  React.useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) throw new Error("MediaMarkdownListSection requires window.matchMedia");
    const mql = window.matchMedia(MOBILE_QUERY);
    const sync = () => setIsMobile(mql.matches);
    sync();
    mql.addEventListener("change", sync);
    return () => mql.removeEventListener("change", sync);
  }, []);

  React.useEffect(() => {
    if (activeIndex === null) return;
    const onKey = (event) => { if (event.key === "Escape") setActiveIndex(null); };
    document.addEventListener("keydown", onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [activeIndex]);

  const getItem = (index) => {
    const item = items[index];
    if (!item || typeof item !== "object") throw new Error(`MediaMarkdownListSection item ${index} missing`);
    if (!item.mediaType) throw new Error(`MediaMarkdownListSection item ${index} missing mediaType`);
    if (!item.mediaSrc) throw new Error(`MediaMarkdownListSection item ${index} missing mediaSrc`);
    if (item.mediaType === "video" && !item.mediaPoster) throw new Error(`MediaMarkdownListSection item ${index} missing mediaPoster`);
    if (!item.rightMarkdown && !item.rightComponent) throw new Error(`MediaMarkdownListSection item ${index} missing right content`);
    if (item.rightComponent && typeof renderRight !== "function") throw new Error("MediaMarkdownListSection requires renderRight for rightComponent");
    return item;
  };

  const renderRightContent = (item) => {
    if (item.rightComponent) return renderRight(item.rightComponent, item.rightProps || {});
    return <ReactMarkdown>{String(item.rightMarkdown)}</ReactMarkdown>;
  };

  const renderThumb = (item) => (
    <img
      src={item.mediaType === "video" ? item.mediaPoster : item.mediaSrc}
      alt={item.mediaAlt || ""}
      className="block h-auto w-full rounded-xl object-cover"
    />
  );

  const renderMedia = (item) => {
    if (item.mediaType !== "video") return renderThumb(item);
    return (
      <video controls poster={item.mediaPoster} className="block h-auto w-full rounded-xl object-cover">
        <source src={item.mediaSrc} />
      </video>
    );
  };

  if (isMobile && activeIndex !== null) {
    const item = getItem(activeIndex);
    return (
      <section
        className="fixed inset-0 z-[9999] grid grid-rows-[auto_1fr] gap-[18px] overflow-auto bg-[#111] p-4 text-white"
        role="dialog"
        aria-modal="true"
      >
        <button
          type="button"
          onClick={() => setActiveIndex(null)}
          className="absolute right-3 top-3 h-9 w-9 rounded-full border-0 bg-white text-[#111] font-bold"
          aria-label="Close"
        >
          x
        </button>
        <div className="w-full">{renderMedia(item)}</div>
        <div className="rounded-xl bg-[#1b1b1b] p-4">{renderRightContent(item)}</div>
      </section>
    );
  }

  return (
    <section className="grid gap-6">
      {items.map((_, index) => {
        const item = getItem(index);
        return (
          <div key={item.id || index} className="grid grid-cols-2 items-start gap-5">
            <button
              type="button"
              className="cursor-pointer border-0 bg-transparent p-0"
              onClick={isMobile ? () => setActiveIndex(index) : undefined}
              aria-label={item.mediaAlt || "Open item"}
            >
              {renderThumb(item)}
            </button>
            {!isMobile && <div>{renderRightContent(item)}</div>}
          </div>
        );
      })}
    </section>
  );
}
